import { Button } from "@/components/ui/Button";
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { useAppContext } from "@/context/AppContext";
import { useLanguage } from "@/context/LanguageContext";
import { useMenuItemsContext } from "@/context/MenuItemsContext";
import Link from "next/link";
import Script from "next/script";
import React, { useEffect } from "react";

export function ChangePaymentMethod() {
  const { language } = useLanguage();
  const copy = {
    cs: {
      deleteAccount: "Smazat účet",
      title: "Změnit platební údaje",
      deleteDescription:
        "Tato akce smaže váš účet. Pokračujte pouze, pokud jste si jisti.",
      description: "Upravte své údaje níže.",
      membership: "Členství",
      standard: "Standardní",
      premium: "Premiové",
      professional: "Profesionální",
      agreeWith: "Souhlasím s",
      terms: "obchodními podmínkami",
      recurringPayment: "opakovanou platbou",
      cancelChanges: "Zrušit změny",
      saveChanges: "Uložit změny",
    },
    en: {
      deleteAccount: "Delete account",
      title: "Change payment details",
      deleteDescription:
        "This action will delete your account. Continue only if you are sure.",
      description: "Edit your details below.",
      membership: "Membership",
      standard: "Standard",
      premium: "Premium",
      professional: "Professional",
      agreeWith: "I agree with",
      terms: "terms and conditions",
      recurringPayment: "recurring payment",
      cancelChanges: "Cancel changes",
      saveChanges: "Save changes",
    },
  }[language];
  const { isLoading, formData, changeMembership } = useAppContext();
  const { handleDiscardChanges, selectedItem } = useMenuItemsContext();
  const { userInfoGlobal } = useAppContext();

  const [canProceed, setCanProceed] = React.useState(false);
  const [termsAgreementChecked, setTermsAgreementChecked] =
    React.useState(false);
  const [paymentTermsAgreementChecked, setPaymentTermsAgreementChecked] =
    React.useState(false);

  function checkCanProceed() {
    if (termsAgreementChecked && paymentTermsAgreementChecked && !isLoading) {
      setCanProceed(true);
    } else {
      setCanProceed(false);
    }
  }

  useEffect(() => {
    checkCanProceed();
  }, [termsAgreementChecked, paymentTermsAgreementChecked, formData]);

  const membershipOptions = [
    { value: "STANDARD", label: copy.standard },
    { value: "PREMIUM", label: copy.premium },
    { value: "PROFESSIONAL", label: copy.professional },
  ];

  const selectedMembership = membershipOptions.find(
    (option) => option.value === userInfoGlobal?.membership,
  )?.label;

  return (
    <>
      <Script
        src="https://gate.gopay.cz/gp-gw/js/embed.js"
        // src="https://gw.sandbox.gopay.com/gp-gw/js/embed.js"
        strategy="afterInteractive"
      />
      <DialogContent className="sm:max-w-[425px]">
        <form
          onSubmit={changeMembership(true)}
          id="payment-form"
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle>{copy.title}</DialogTitle>
            <DialogDescription>
              {selectedItem?.name === "Smazat účet" ||
              selectedItem?.name === copy.deleteAccount
                ? copy.deleteDescription
                : copy.description}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-3 py-4">
            <div className="app-form-row">
              <Label htmlFor="subject" className="text-right">
                Email
              </Label>
              <Input
                id="email"
                name="email"
                className="col-span-3"
                value={formData.email || ""}
                disabled
              />
            </div>
            <div className="app-form-row">
              <Label htmlFor="subject" className="text-right">
                {copy.membership}
              </Label>
              <Input
                id="watchDogLevel"
                name="watchDogLevel"
                className="col-span-3"
                value={selectedMembership || ""}
                disabled
              />
            </div>
            <div className="flex items-center justify-center w-full">
              <div className="flex items-center">
                <Label className="flex items-center cursor-pointer gap-3">
                  <Input
                    type="checkbox"
                    checked={termsAgreementChecked}
                    onChange={(e) => setTermsAgreementChecked(e.target.checked)}
                    className="size-4 text-foreground border-input rounded"
                  />
                  <span className="text-sm text-foreground flex flex-wrap gap-1">
                    {copy.agreeWith}
                    <Link
                      href="/obchodni-podminky"
                      className="text-foreground hover:text-foreground/80 text-sm hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {copy.terms}
                    </Link>
                  </span>
                </Label>
              </div>
            </div>

            <div className="flex items-center justify-center w-full">
              <div className="flex items-center">
                <Label className="flex items-center cursor-pointer gap-3 w-full flex-wrap">
                  <Input
                    type="checkbox"
                    checked={paymentTermsAgreementChecked}
                    onChange={(e) =>
                      setPaymentTermsAgreementChecked(e.target.checked)
                    }
                    className="size-4 text-foreground border-input rounded"
                  />
                  <span className="text-sm text-foreground flex flex-wrap gap-1">
                    {copy.agreeWith}{" "}
                    <Link
                      href="/podminky-predplatneho"
                      className="text-foreground hover:text-foreground/80 text-sm hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {copy.recurringPayment}
                    </Link>
                  </span>
                </Label>
              </div>
            </div>
          </div>
          <DialogFooter className="flex flex-row justify-end items-center gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleDiscardChanges}
            >
              {copy.cancelChanges}
            </Button>
            <Button
              type="submit"
              disabled={!canProceed}
              variant="default"
              className="relative"
            >
              {isLoading ? (
                <>
                  <span className="opacity-0">{copy.saveChanges}</span>
                  <span className="dots">
                    <span className="dot"></span>
                    <span className="dot"></span>
                    <span className="dot"></span>
                  </span>
                </>
              ) : (
                copy.saveChanges
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </>
  );
}
